import { toast } from "react-toastify";
import {
  apiGet,
  apiRawGet,
  apiRawPost,
  apiRawDelete
} from "./apiClient";

export interface FileMetadata {
  id: number;
  fileId: string;
  fileName: string;
  mimeType: string;
  size: number;
  uploaded: string;
  templateType?: string;
}

export interface UploadResult {
  fileId: string;
  fileName: string;
}

/* ============================================================
   PROTECTED — Get all files
============================================================ */
const GetAllFiles = async (): Promise<FileMetadata[] | undefined> => {
  try {
    const files = await apiGet<FileMetadata[]>(`/File/get-all`);
    return files;
  } catch (err) {
    console.error("Error fetching files", err);
    toast.error("Något gick fel vid hämtning av filer");
    return undefined;
  }
};

/* ============================================================
   PROTECTED — Download file
   (Blob, so we use raw GET!)
============================================================ */
const DownloadFile = async (fileId: string): Promise<Blob | undefined> => {
  try {
    const res = await apiRawGet(`/File/get-file/${fileId}`);

    if (!res.ok) {
      toast.warning(`Nedladdning misslyckades: ${res.status} ${res.statusText}`);
      return undefined;
    }

    return await res.blob();
  } catch (err) {
    console.error("Error downloading file", err);
    toast.error("Något gick fel vid nedladdning av fil");
    return undefined;
  }
};

/* ============================================================
   PROTECTED — Upload file (multipart/form-data)
============================================================ */
const UploadFile = async (
  file: File,
  templateType?: string
): Promise<UploadResult | undefined> => {
  const formData = new FormData();
  formData.append("file", file);
  if (templateType) formData.append("templateType", templateType);

  try {
    const res = await apiRawPost(`/File`, formData);

    if (!res.ok) {
      const error = await res.json().catch(() => ({}));
      toast.warning(`Upload misslyckades: ${JSON.stringify(error)}`);
      return undefined;
    }

    const result = await res.json();
    toast.success(`Filen "${result.fileName}" laddades upp!`);
    return result as UploadResult;
  } catch (err) {
    console.error("Error uploading file", err);
    toast.error("Något gick fel vid uppladdning av fil");
    return undefined;
  }
};

/* ============================================================
   PROTECTED — Delete file
============================================================ */
const DeleteFile = async (fileId: string): Promise<boolean> => {
  try {
    const res = await apiRawDelete(`/File/delete/${fileId}`);

    if (res.status === 204) {
      toast.success("Filen raderades!");
      return true;
    }

    toast.warning(`Kunde inte radera fil (${res.status})`);
    return false;
  } catch (err) {
    console.error("Error deleting file", err);
    toast.error("Något gick fel vid radering av fil");
    return false;
  }
};

export const FileService = {
  GetAllFiles,
  DownloadFile,
  UploadFile,
  DeleteFile
};
